import { Box, Center, Flex, Text, AspectRatio } from '@chakra-ui/react'
import { faCamera } from '@fortawesome/pro-regular-svg-icons'

import Placeholder from 'src/assets/image/PlaceholderGenericSVG'
import Icon from 'src/components/widgets/Icon'
import ItListImage from 'src/components/widgets/ItListImage/ItListImage'
import PhotoUpload from 'src/components/widgets/PhotoUpload/PhotoUpload'
import { AssetType } from 'src/lib/assets/assetsTypes'
import useField from 'src/lib/hooks/form/useField'

import Hint from './Hint/Hint'

const EditItemImage: React.FC<{
  name: string
  assetType: AssetType
  assetId: number
  borderRadius?: number
  width: number
  height: number
  skipCrop?: boolean
  title?: string
  hint?: string
  hintLink?: string
  placeholderSrc?: string
  placeholderMaxWidth?: number
  allowVariableWidth?: boolean
}> = ({
  name,
  assetType,
  assetId,
  width,
  height,
  skipCrop = false,
  borderRadius = 8,
  placeholderSrc,
  placeholderMaxWidth,
  title,
  hint,
  hintLink,
  allowVariableWidth = false,
}) => {
  const field = useField<string>(name)
  const imageSrc = field.value || placeholderSrc
  const aspectRatio = placeholderMaxWidth ? placeholderMaxWidth / height : 1
  const elWidth = placeholderMaxWidth || 100
  const elHeight = 100

  return (
    <Flex
      borderRadius={12}
      borderStyle="solid"
      borderWidth="1px"
      borderColor={field?.error?.message ? 'danger' : 'sandstonePlus'}
      mt={4}
      p={4}
      flexDirection="row"
      justifyContent="space-between"
    >
      <Flex justifyContent="space-between" flexDirection="column">
        <Box>
          <Flex mb={2}>
            <Text fontWeight={900} className="body-small-emphasis">
              {title || 'Image'}
            </Text>
          </Flex>
          <Hint hint={hint} hintLink={hintLink} width={width} height={height} />
        </Box>
        {field?.error?.message && (
          <Text color="danger" className="body-small-emphasis">
            {field.error.message.toString()}
          </Text>
        )}
      </Flex>
      <PhotoUpload
        data-testid={`${name}.imageUrl`}
        assetType={assetType}
        assetId={assetId}
        onFileUploaded={(uploadedData, _record) => {
          field?.onChange(uploadedData.url)
        }}
        width={allowVariableWidth ? undefined : width}
        height={height}
        skipCrop={skipCrop}
      >
        <Box
          position="relative"
          filter="brightness(0.97)"
          borderRadius={borderRadius}
          overflow="hidden"
          w={allowVariableWidth && imageSrc ? 'auto' : elWidth}
          maxW={elWidth}
          h={elHeight}
          boxSizing="content-box"
          data-testid="product-photoUpload-photoUploaded"
          backgroundColor="air"
        >
          {!imageSrc && (
            <AspectRatio
              ratio={aspectRatio}
              overflow="clip"
              borderRadius={borderRadius}
            >
              <Placeholder style={{ transform: 'scale(2)' }} />
            </AspectRatio>
          )}
          {imageSrc && (
            <ItListImage
              src={imageSrc}
              htmlWidth={allowVariableWidth ? undefined : width}
              htmlHeight={height}
              alt={`cover preview for item ${name}`}
              w={allowVariableWidth ? undefined : elWidth}
              h={elHeight}
              objectFit={placeholderMaxWidth ? 'contain' : 'cover'}
              borderRadius={borderRadius}
            />
          )}
          <Center
            boxSize="36px"
            borderColor="black"
            borderRadius="full"
            backgroundColor="black"
            opacity={0.7}
            w={12}
            h={12}
            position="absolute"
            bottom={0}
            right={0}
            data-testid="product-photoUpload-editPhotoButton"
          >
            <Icon icon={faCamera} fontSize="16px" color="white" />
          </Center>
        </Box>
      </PhotoUpload>
    </Flex>
  )
}

export default EditItemImage
